import { Directive, ElementRef, OnDestroy, OnInit, Inject, PLATFORM_ID, input, NgZone, afterNextRender } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';

@Directive({
  selector: '[followCursor]',
  standalone: true,
})
export class FollowCursorDirective implements OnDestroy {
  duration = input<number>(1);
  ease = input<string>('power3.out');
  rotate = input<number>(0);

  disabled = input<boolean>(false);

  private currentTween?: gsap.core.Tween;
  private isInitialized = false;
  private lastMouseX: number | null = null;
  private lastMouseY: number | null = null;
  private lastTimestamp: number | null = null;
  private resetTimeout: any = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone
  ) {
    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId) && this.supportsHover()) {
        // Initial setup binnen Angular zone
        this.initCursorFollow();
      }
    });
  }
  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.cleanup();
    }
  }

  private supportsHover(): boolean {
    if (window.matchMedia) {
      return window.matchMedia('(hover: hover)').matches;
    }
    return !('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }

  private initCursorFollow(): void {
    // Mouse events BUITEN Angular zone voor maximale performance
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.onMouseMove, { passive: true });
    });

    this.isInitialized = true;
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (this.disabled() || !this.isInitialized) return;

    // Clear any pending reset
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = null;
    }

    const now = performance.now();
    let rotation = 0;

    if (this.lastMouseX !== null && this.lastMouseY !== null && this.lastTimestamp !== null) {
      const dx = event.clientX - this.lastMouseX;
      const dy = event.clientY - this.lastMouseY;
      const dt = now - this.lastTimestamp;
      const velocity = Math.sqrt(dx * dx + dy * dy) / (dt || 1); // px/ms
      // Map velocity to a rotation angle, clamp for sanity
      rotation = Math.max(this.rotate() * -1, Math.min(this.rotate(), dx * velocity * 0.5));
    }

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;
    this.lastTimestamp = now;

    if (!this.el.nativeElement.classList.contains('follow-cursor--inited')) {
      this.el.nativeElement.classList.add('follow-cursor--inited');
      gsap.set(this.el.nativeElement, {
        rotate: 0,
        x: event.clientX,
        y: event.clientY,
      });
    }

    const tweenVars: gsap.TweenVars = {
      x: event.clientX,
      y: event.clientY,
      duration: this.duration(),
      ease: this.ease(),
      overwrite: true,
    };
    if (this.rotate()) {
      tweenVars.rotate = rotation;
    }

    this.currentTween = gsap.to(this.el.nativeElement, tweenVars);

    // Schedule a reset to 0 rotation if no movement for 100ms
    if (this.rotate()) {
      this.resetTimeout = setTimeout(() => {
        gsap.to(this.el.nativeElement, {
          rotate: 0,
          duration: 0.3,
          ease: this.ease(),
          overwrite: true,
        });
      }, 100);
    }
  };

  private cleanup(): void {
    // Cleanup ook buiten zone
    this.ngZone.runOutsideAngular(() => {
      document.removeEventListener('mousemove', this.onMouseMove);
    });

    // Kill alle GSAP tweens
    this.currentTween?.kill();
    gsap.killTweensOf(this.el.nativeElement);

    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
      this.resetTimeout = null;
    }

    this.isInitialized = false;
    this.lastMouseX = null;
    this.lastMouseY = null;
    this.lastTimestamp = null;
  }
}
